import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Tooltip } from "react-tooltip";
import { Theme } from "../../../components/atoms/theme";
import { ParagraphS, TitleM } from "../../../components/atoms/Typography";
import { ContentWrapper } from "../../../components/ContentWrapper";
import { ModalWithTwoButtons } from "../../../components/molecules/Modal";
import { Select } from "@tyrannosaurustech/ui";
import { CoreTable } from "../../../components/organisms/CoreTable";
import { useAppSelector } from "../../../hooks/redux";
import { setPageSize, setSearchParam } from "../../../redux/reducers/filter";
import {
  useDeleteRequestMutation,
  useBulkUpdateRequestsMutation,
} from "../../../redux/reduxQuery/requestApi";
import { filterDuplicateSelectOptions } from "../../../utils/filterDuplicateSelectOptions";
import { filtersRequest } from "../../../utils/filtersRequest";
import { statusColor } from "../../../utils/statusColor";
import { statusLabel } from "../../../utils/statusLabel";
import { useActionButtons } from "./table/actionButtons";
import { useColumns } from "./table/columns";
import { useGetData } from "./table/data";

export const Status = (props) => {
  return (
    <ParagraphS
      color={statusColor[props.status]}
      style={{ margin: 0, textTransform: "capitalize" }}
    >
      {statusLabel[props.status]}
    </ParagraphS>
  );
};
interface selectProps {
  value: string;
  label: string;
}

export default function Request() {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [requestId] = useState("");
  const [requestStatus] = useState("");
  const [requestPatientName] = useState("");
  const [requestFirmName] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  const [deleteRequest] = useDeleteRequestMutation();
  const [bulkUpdate] = useBulkUpdateRequestsMutation();
  const [patients, setPatients] = useState<selectProps[]>([]);
  const [medicalProvider, setMedicalProvider] = useState<selectProps[]>([]);
  const [totalAmountOfRecords, setTotalAmountOfRecords] = useState(0);
  const [openBulkStatusModal, setOpenBulkStatusModal] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkIds, setBulkIds] = useState<string[]>([]);
  const [clearSelectionFn] = useState<(() => void) | null>(null);
  const globalUserFilter = useAppSelector((state) => state.filter);
  const reshapedGlobalUserFilter = useMemo(() => {
    const reshapedFilter =
      (globalUserFilter["request"] &&
        JSON.parse(JSON.stringify(globalUserFilter["request"]))) ??
      {};
    if (globalUserFilter["request"]) {
      Object.keys(globalUserFilter["request"]).forEach((key) => {
        const filter = globalUserFilter["request"]?.[key];
        if (typeof filter === "object") {
          if (!filter?.name && reshapedFilter && key.includes(".")) {
            delete reshapedFilter[key];
            reshapedFilter[key.split(".")[0]] = {
              name: filter,
            };
          }
        }
      });
      return reshapedFilter;
    }
  }, [globalUserFilter]);
  const [, setFilterValues] = useState(reshapedGlobalUserFilter ?? {});

  useEffect(() => {
    setFilterValues(reshapedGlobalUserFilter ?? {});
  }, [reshapedGlobalUserFilter]);
  const statePageSize = globalUserFilter.requestControl?.pageSize;
  const filterString = useMemo(() => {
    const result = filtersRequest(
      reshapedGlobalUserFilter ?? {},
      "",
      page,
      Number(statePageSize ?? 50)
    );
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    pageSize,
    reshapedGlobalUserFilter,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    statePageSize,
  ]);
  const { config, refetch } = useGetData(filterString ?? "");
  const { columns } = useColumns(medicalProvider, patients);
  const { loading: isLoading } = config;
  const data: any = config?.data;

  const { actionButtonsConfig, filterButton, openFilters, setOpenFilters } =
    useActionButtons(filterString, config?.asyncConfig?.facets?.search);
  const actionButtonsWithBulk = [
    ...(bulkIds.length > 0
      ? [
          {
            label: "Update Status",
            icon: "PencilIcon",
            onClick: () => setOpenBulkStatusModal(true),
            base: "secondary",
            btnContainerClasses:
              "box-border border-2 border-primary-400 border-solid",
            iconClasses: "h-5 w-5",
          },
        ]
      : []),
    ...actionButtonsConfig,
  ];
  const actionRightButtons = [filterButton];

  const getFillSelect = useCallback(() => {
    if (data) {
      setTotalAmountOfRecords(data.total);
      const patientOptions = data.map((item) => ({
        value: item.patient?.id,
        label: item.patient?.name,
      }));
      setPatients((prevState) =>
        filterDuplicateSelectOptions([...prevState, ...patientOptions])
      );
      const providerOptions = data?.map((item) => ({
        value: item.provider?.id,
        label: item.provider?.name,
      }));
      setMedicalProvider((prevState) =>
        filterDuplicateSelectOptions([...prevState, ...providerOptions])
      );
    }
  }, [data]);

  useEffect(() => {
    if (isLoading) {
      return;
    } else {
      getFillSelect();
    }
  }, [isLoading, getFillSelect]);

  const currentPageSize = config?.asyncConfig?.pageSize ?? 50;
  const currentSearch = config?.asyncConfig?.facets?.search;

  useEffect(() => {
    dispatch(
      setPageSize({
        requestControl: { pageSize: currentPageSize },
      })
    );
  }, [currentPageSize, dispatch]);

  useEffect(() => {
    dispatch(
      setSearchParam({
        requestControl: { search: currentSearch },
      })
    );
  }, [currentSearch, dispatch]);

  useEffect(() => {
    config.asyncConfig.onPaginationChange({
      pageSize: Number(globalUserFilter.requestControl?.pageSize ?? 50),
      pageIndex: 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      {isOpen && (
        <ModalWithTwoButtons
          title="delete request"
          content={`Are you sure you want to delete with 
            status ${requestStatus} for ${requestPatientName} 
            at ${requestFirmName}? If you do you won’t be able to recover it.`}
          button1Text="delete"
          button2Text="cancel"
          loader={isProcessing}
          onClick1={async () => {
            setIsProcessing(true);
            await deleteRequest(requestId);
            refetch();
            setIsProcessing(false);
            setIsOpen(false);
          }}
          onClick2={() => setIsOpen(false)}
        />
      )}
      <ContentWrapper>
        <TitleM style={{ margin: "1rem 0" }}>Requests</TitleM>
        <CoreTable
          key={filterString}
          tableType="request"
          columns={columns}
          isLoading={isLoading}
          setPage={setPage}
          totalAmountOfRecords={totalAmountOfRecords}
          searchByPage={true}
          setFilterValues={setFilterValues}
          actionButtonsConfig={actionButtonsWithBulk}
          actionRightButtons={actionRightButtons}
          actionRightAccessories={null}
          openFilters={openFilters}
          setOpenFilters={setOpenFilters}
          pageSize={Number(globalUserFilter.requestControl?.pageSize ?? 50)}
          selectConfig={{
            handleRowsSelect: (rows) => {
              setBulkIds(rows.map((r) => r.original.id));
            },
            bulkButtons: () => [],
          }}
          hideSelectionBar
          footerCenterContent={
            bulkIds.length > 0 ? `${bulkIds.length} selected` : undefined
          }
          {...config}
        />
      </ContentWrapper>

      {openBulkStatusModal && (
        <ModalWithTwoButtons
          title="update status"
          content=""
          button1Text="Cancel"
          button2Text="Update"
          loader={isProcessing}
          onClick1={() => {
            setOpenBulkStatusModal(false);
            setBulkStatus("");
          }}
          onClick2={async () => {
            if (!bulkStatus) {
              setOpenBulkStatusModal(false);
              return;
            }
            setIsProcessing(true);
            try {
              await bulkUpdate({ ids: bulkIds, status: bulkStatus });
              await refetch();
              if (clearSelectionFn) clearSelectionFn();
              setOpenBulkStatusModal(false);
              setBulkIds([]);
              setBulkStatus("");
            } finally {
              setIsProcessing(false);
            }
          }}
        >
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              marginBottom: "3rem",
            }}
          >
            <div style={{ width: "96%" }}>
              <Select
                label=""
                value={
                  bulkStatus
                    ? {
                        id: bulkStatus,
                        label: statusLabel[bulkStatus] as string,
                      }
                    : undefined
                }
                placeholder="New Status"
                onChange={(opt) => setBulkStatus(String(opt.id))}
                options={Object.entries(statusLabel).map(([value, label]) => ({
                  id: value,
                  label: label as string,
                }))}
                className="w-full"
                size={"md" as any}
              />
            </div>
          </div>
        </ModalWithTwoButtons>
      )}

      <Tooltip
        id="request-tooltip"
        style={{
          zIndex: 1000,
          padding: "1rem",
          borderRadius: "0.75rem",
          backgroundColor: Theme.colors.neutrals_000,
        }}
        opacity={1.0}
      />
    </>
  );
}
